import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, getToken } from '../../lib/api';
import type { Attachment } from '../../lib/types';
import { attachmentKindLabel, bytesLabel } from '../../lib/format';
import { Spinner, EmptyState } from '../../components/ui';
import { isFinanceGateDisabled } from './FinanceGate';

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

// 允许登记的业务附件种类。与后端 attachments.ENTITY_TYPES 对应；
// 三模式附件包（合同 PDF + 客户 PO + QC 交货文件）的**组成规则**属未定口径，
// 因此这里只提供「按种类逐个登记」，不提供「一键生成附件包」。
const KIND_OPTIONS = [
  { value: 'arca_voucher', label: 'ARCA 原票' },
  { value: 'statement', label: '对账单' },
  { value: 'customer_po', label: '客户 PO' },
  { value: 'delivery_signature', label: '交货签字' },
  { value: 'contract_pdf', label: '合同 PDF' },
  { value: 'general', label: '其他' },
];

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * 附件面板：登记 / 列表 / 下载。
 * 下载走 fetch + Bearer 令牌再转 blob —— 直接 <a href> 不会带 Authorization 头，
 * 会得到 401 而看起来像「文件不见了」。
 */
export function AttachmentPanel({ entityType, entityId, canUpload }: {
  entityType: string;
  entityId: string;
  canUpload: boolean;
}) {
  const [rows, setRows] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [gateClosed, setGateClosed] = useState(false);
  const [kind, setKind] = useState('arca_voucher');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await api.list<Attachment>('/api/attachments', { entity_type: entityType, entity_id: entityId });
      setRows(data);
      setGateClosed(false);
    } catch (e) {
      if (isFinanceGateDisabled(e)) setGateClosed(true);
      else setErr(e instanceof ApiError ? e.message : '附件加载失败');
      setRows([]);
    } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { void load(); }, [load]);

  const upload = async (file: File) => {
    setErr(null);
    if (file.size > MAX_UPLOAD_BYTES) { setErr(`单个附件不得超过 ${MAX_UPLOAD_BYTES / 1024 / 1024}MB`); return; }
    setBusy(true);
    try {
      await api.post('/api/attachments', {
        entity_type: entityType,
        entity_id: entityId,
        kind,
        filename: file.name,
        mime_type: file.type || null,
        content_base64: await readAsBase64(file),
      });
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '附件上传失败');
    } finally { setBusy(false); }
  };

  const download = async (row: Attachment) => {
    setErr(null);
    try {
      const res = await fetch(`/api/attachments/${row.id}/download`, {
        headers: { authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || `下载失败 ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.original_name;
      a.click();
      // 立即 revoke 会让部分浏览器在下载真正开始前就丢掉 blob，
      // 表现为「点了下载没反应」。延后释放，让下载先取走字节。
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '下载失败');
    }
  };

  if (gateClosed) {
    return <div className="muted" style={{ fontSize: 12.5 }}>附件能力未在本环境启用（INTEGRAL_ENABLE_FINANCE_READ）。</div>;
  }

  return (
    <div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
        附件登记（元数据 + 校验和）。文件字节存于服务端独立目录，下载时逐字节核对 sha256。
        「必需文件缺失时如何处理」属未定口径，本面板不代替该判定。
      </div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {canUpload ? (
        <div className="toolbar" style={{ alignItems: 'flex-end', gap: 8, marginBottom: 8 }}>
          <label className="field" style={{ maxWidth: 180 }}>
            <label>附件种类</label>
            <select className="sel" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>选择文件</label>
            <input ref={fileRef} className="txt" type="file" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
          </label>
          {busy ? <Spinner label="上传中…" /> : null}

        </div>
      ) : null}
      {loading ? <Spinner label="读取附件…" /> : rows.length === 0 ? (
        <EmptyState>暂无附件。{canUpload ? '可选文件登记原件。' : ''}</EmptyState>
      ) : (
        <AttachmentRows rows={rows} onDownload={(row) => void download(row)} />
      )}
    </div>
  );
}

function AttachmentRows({ rows, onDownload }: { rows: Attachment[]; onDownload: (row: Attachment) => void }) {
  return (
    <table className="lines">
      <thead>
        <tr>
          <th>种类</th><th>原始文件名</th><th style={{ textAlign: 'right' }}>大小</th>
          <th>sha256（前16位）</th><th>上传</th><th style={{ textAlign: 'right' }}>操作</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{attachmentKindLabel(r.kind)}</td>
            <td>{r.original_name}</td>
            <td style={{ textAlign: 'right' }}>{bytesLabel(r.byte_size)}</td>
            <td className="num">{String(r.sha256).slice(0, 16)}…</td>
            <td>{r.uploaded_by_label || r.source_system || '—'}</td>
            <td style={{ textAlign: 'right' }}>
              <button className="btn" onClick={() => onDownload(r)}>下载</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
